/**
 * Fetch wrapper that automatically handles authentication and token expiration
 */
import authService from '../services/authService';
import { showSessionExpiredNotification } from './notification';

/**
 * Enhanced fetch that handles authentication errors automatically
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @returns {Promise<Response>} - The fetch response
 */
export const fetchWithAuth = async (url, options = {}) => {
  try {
    const response = await fetch(url, options);
    
    // Handle 401 Unauthorized errors
    if (response.status === 401) {
      const errorData = await response.json().catch(() => ({}));
      const isTokenExpired = errorData.code === 'TOKEN_EXPIRED' || 
                            errorData.error === 'Invalid or expired token' ||
                            errorData.message?.includes('expired') ||
                            errorData.message?.includes('token');
      
      // Show user-friendly notification for expired tokens
      if (isTokenExpired) {
        await showSessionExpiredNotification();
      }
      
      // Determine which auth to clear based on current path
      if (window.location.pathname.startsWith('/admin')) {
        authService.adminLogout();
        window.location.href = '/admin/login';
      } else if (!window.location.pathname.startsWith('/login') && 
                !window.location.pathname.startsWith('/register') &&
                !window.location.pathname.startsWith('/member-login')) {
        authService.memberLogout();
        window.location.href = '/member-login';
      }
      
      throw new Error(errorData.error || 'Authentication failed');
    }
    
    return response;
  } catch (error) {
    // Re-throw the error for the caller to handle
    throw error;
  }
};

/**
 * Helper to get the appropriate auth token based on current route
 * @returns {string|null} - The auth token or null
 */
export const getAuthToken = () => {
  if (window.location.pathname.startsWith('/admin')) {
    return authService.getAdminToken();
  }
  return authService.getMemberToken();
};

/**
 * Helper to add auth headers to fetch options
 * @param {object} options - Existing fetch options
 * @returns {object} - Options with auth headers added
 */
export const addAuthHeaders = (options = {}) => {
  const token = getAuthToken();
  
  if (token) {
    return {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`
      }
    };
  }
  
  return options;
};

export default fetchWithAuth;
